// Shared constants for PurchaseRequest status display

// Expanded MVP statuses, keeping legacy ones for compatibility
export const PURCHASE_REQUEST_STATUS = [
  // Legacy ingestion states (still in use by processor)
  "uploaded",
  "processing",
  "quoted",
  "completed",
  // Quick create auto-pipeline (system-managed) states
  // quick_draft: reserved for a pre-processing placeholder (not currently used)
  // quick_processing: background job extracting items from free text
  // quick_ready: draft ready for user review/confirmation
  // quick_error: processing failed; user can retry/edit
  "quick_draft",
  "quick_processing",
  "quick_ready",
  "quick_error",
  // Requester
  "draft",
  "submitted",
  "needs_clarification",
  "cancelled_by_requester",
  // Buyer
  "triage",
  "quoting",
  "quotes_received",
  "selecting_suppliers",
  "awaiting_supervisor",
  "pending_approval",
  // Supervisor
  "approved",
  "rejected",
  // Operations / Close
  "ordered",
  "partially_fulfilled",
  "fulfilled",
  "cancelled",
] as const;

export type PurchaseRequestStatus = typeof PURCHASE_REQUEST_STATUS[number];

const PURCHASE_REQUEST_STATUS_HIDDEN_FROM_PICKER = [
  "pending_approval",
  "approved",
  "rejected",
  "awaiting_supervisor"
] as const satisfies readonly PurchaseRequestStatus[];

export const PURCHASE_REQUEST_STATUS_PICKER = PURCHASE_REQUEST_STATUS.filter(
  (status) => !(PURCHASE_REQUEST_STATUS_HIDDEN_FROM_PICKER as readonly string[]).includes(status),
);

/**
 * Statuses where the purchase request has been approved (approval flow completed successfully)
 * and export of purchase orders should be allowed.
 * 
 * This includes:
 * - 'approved': explicitly approved via approval flow
 * - operational states that follow approval (ordered, partially_fulfilled, fulfilled, completed)
 */
export const PURCHASE_REQUEST_EXPORT_ALLOWED_STATUSES: readonly PurchaseRequestStatus[] = [
  "approved",
  "ordered",
  "partially_fulfilled",
  "fulfilled",
  "completed",
] as const;

interface StatusMeta {
  label: string;
  color: string; // tailwind classes for badge colors
  group: "requester" | "buyer" | "supervisor" | "operations" | "system";
  desc?: string; // short description for StatusPicker
}

export const PURCHASE_REQUEST_STATUS_META: Record<PurchaseRequestStatus, StatusMeta> = {
  // Legacy/system states
  uploaded: { label: "Cargado", color: "bg-blue-100 text-blue-800", group: "system", desc: "Solicitud creada desde documento" },
  processing: { label: "Procesando", color: "bg-yellow-100 text-yellow-800", group: "system", desc: "Extrayendo/normalizando datos" },
  quoted: { label: "Cotizado", color: "bg-green-100 text-green-800", group: "system", desc: "Hay al menos una cotización" },
  completed: { label: "Completado", color: "bg-gray-100 text-gray-800", group: "operations", desc: "Ciclo finalizado" },
  
  // Quick create pipeline (system-managed)
  // Nota: estos estados aparecen mientras se procesa la "Solicitud rápida" y
  // sirven solo para guiar al usuario hasta que la solicitud pasa a "uploaded".
  quick_draft: { label: "Borrador (rápido)", color: "bg-slate-100 text-slate-800", group: "system", desc: "Inicializando borrador" },
  quick_processing: { label: "Procesando (rápido)", color: "bg-sky-100 text-sky-800", group: "system", desc: "Extrayendo ítems desde el texto" },
  quick_ready: { label: "Borrador listo", color: "bg-teal-100 text-teal-800", group: "system", desc: "Resultados generados; revisar y confirmar" },
  quick_error: { label: "Error (rápido)", color: "bg-rose-100 text-rose-800", group: "system", desc: "Falló el procesamiento; editar o reintentar" },

  // Requester
  draft: { label: "Borrador", color: "bg-slate-100 text-slate-800", group: "requester", desc: "Aún en edición" },
  submitted: { label: "Enviada", color: "bg-blue-100 text-blue-800", group: "requester", desc: "Lista para revisión de compras" },
  needs_clarification: { label: "Aclaraciones requeridas", color: "bg-amber-100 text-amber-900", group: "requester", desc: "Falta info del solicitante" },
  cancelled_by_requester: { label: "Cancelada por solicitante", color: "bg-rose-100 text-rose-800", group: "requester", desc: "El solicitante canceló" },

  // Buyer
  triage: { label: "En revisión", color: "bg-indigo-100 text-indigo-800", group: "buyer", desc: "Compras verifica alcance" },
  quoting: { label: "Cotizando", color: "bg-yellow-100 text-yellow-900", group: "buyer", desc: "Solicitando ofertas" },
  quotes_received: { label: "Cotizaciones recibidas", color: "bg-emerald-100 text-emerald-800", group: "buyer", desc: "Ofertas para comparar" },
  selecting_suppliers: { label: "Selección en curso", color: "bg-teal-100 text-teal-800", group: "buyer", desc: "Armando propuesta de compra" },
  awaiting_supervisor: { label: "Pendiente de aprobación", color: "bg-purple-100 text-purple-800", group: "buyer", desc: "Esperando aprobación" },
  pending_approval: { label: "Aprobación en curso", color: "bg-purple-200 text-purple-900", group: "supervisor", desc: "Flujo de aprobación activo" },

  // Supervisor
  approved: { label: "Aprobada", color: "bg-green-100 text-green-800", group: "supervisor", desc: "Autorizada por supervisor" },
  rejected: { label: "Rechazada", color: "bg-red-100 text-red-800", group: "supervisor", desc: "Requiere ajustes" },

  // Ops / Close
  ordered: { label: "Orden emitida", color: "bg-cyan-100 text-cyan-800", group: "operations", desc: "Se emitió la OC" },
  partially_fulfilled: { label: "Entrega parcial", color: "bg-amber-100 text-amber-900", group: "operations", desc: "Recepción parcial" },
  fulfilled: { label: "Completada", color: "bg-green-100 text-green-800", group: "operations", desc: "Recepción completa" },
  cancelled: { label: "Cancelada", color: "bg-gray-200 text-gray-800", group: "operations", desc: "Cierre sin compra" },
};

export const PURCHASE_REQUEST_STATUS_LABELS: Record<PurchaseRequestStatus, string> =
  Object.fromEntries(Object.entries(PURCHASE_REQUEST_STATUS_META).map(([k, v]) => [k, v.label])) as Record<PurchaseRequestStatus, string>;

export const PURCHASE_REQUEST_STATUS_COLORS: Record<PurchaseRequestStatus, string> =
  Object.fromEntries(Object.entries(PURCHASE_REQUEST_STATUS_META).map(([k, v]) => [k, v.color])) as Record<PurchaseRequestStatus, string>;

export const PURCHASE_REQUEST_STATUS_OPTIONS = PURCHASE_REQUEST_STATUS.map((s) => ({
  value: s,
  label: PURCHASE_REQUEST_STATUS_LABELS[s],
}));

export function purchaseRequestStatusLabel(status: string): string {
  return PURCHASE_REQUEST_STATUS_LABELS[status as PurchaseRequestStatus] ?? status;
}

export function purchaseRequestStatusColor(status: string): string | undefined {
  return PURCHASE_REQUEST_STATUS_COLORS[status as PurchaseRequestStatus];
}

const STATUS_SET: ReadonlySet<string> = new Set<string>(PURCHASE_REQUEST_STATUS as readonly string[]);

function isPurchaseRequestStatus(s: string): s is PurchaseRequestStatus {
  return STATUS_SET.has(s);
}

export function purchaseRequestStatusGroup(status: string): StatusMeta["group"] | undefined {
  if (isPurchaseRequestStatus(status)) {
    return PURCHASE_REQUEST_STATUS_META[status].group;
  }
  return undefined;
}

/**
 * Check if purchase order export is allowed for the given status.
 * Returns true if the approval flow has been completed successfully
 * (status is approved or a downstream operational status).
 */
export function canExportPurchaseOrders(status: string): boolean {
  return PURCHASE_REQUEST_EXPORT_ALLOWED_STATUSES.includes(status as PurchaseRequestStatus);
}
